import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { normalizeError } from "../lib/normalizeError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle,
  SheetDescription
} from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "../components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import ItemCardPreviewModal from "../components/ItemCardPreviewModal";
import { 
  Plus, 
  Search, 
  Package, 
  Edit, 
  Printer, 
  AlertTriangle,
  TrendingDown,
  BarChart3,
  Lightbulb,
  Check,
  Info,
  Trash2,
  RotateCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function Items() {
  const { getAuthHeader, user } = useAuth();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [forecasts, setForecasts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [applyingRop, setApplyingRop] = useState(false);
  const [applyingOrderQty, setApplyingOrderQty] = useState(false);
  
  // PDF Preview Modal state
  const [pdfPreview, setPdfPreview] = useState({
    open: false,
    blobUrl: null,
    filename: "",
    itemName: ""
  });
  
  // Print dialog state (for orientation selection)
  const [printDialog, setPrintDialog] = useState({
    open: false,
    item: null,
    orientation: "portrait",
    loading: false
  });
  
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    unit: "each",
    location_id: "",
    department_id: "",
    supplier_id: "",
    supplier_url: "",
    reorder_point: 0,
    reorder_qty: 0,
    max_qty: 0,
    safety_stock: 0,
    depletion_qty: 1,
    lead_time_days: 7,
    tag_uid: "",
    qr_token: "",
    card_size: "",
    card_orientation: "portrait",
    image_url: ""
  });
  const [cardSizes, setCardSizes] = useState([]);

  useEffect(() => {
    fetchData();
    fetchCardSizes();
  }, []);

  const fetchCardSizes = async () => {
    try {
      const res = await axios.get(`${API}/card-sizes`);
      setCardSizes(res.data);
    } catch (e) {
      console.error("Failed to fetch card sizes");
    }
  };

  const fetchData = async () => {
    try {
      const [itemsRes, locsRes, deptsRes, suppRes] = await Promise.all([
        axios.get(`${API}/items`, { withCredentials: true }),
        axios.get(`${API}/locations`, { withCredentials: true }),
        axios.get(`${API}/departments`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true })
      ]);
      
      // Normalize response shape - always ensure we get an array
      const normalizeArray = (data) => {
        // If data is already an array, use it
        if (Array.isArray(data)) return data;
        // If data is an object with items property, use that
        if (data && Array.isArray(data.items)) return data.items;
        // If data is an error object, show toast and return empty array
        if (data && data.ok === false) {
          toast.error(data.message || "Failed to load data");
          return [];
        }
        // Fallback to empty array
        return [];
      };
      
      setItems(normalizeArray(itemsRes.data));
      setLocations(normalizeArray(locsRes.data));
      setDepartments(normalizeArray(deptsRes.data));
      setSuppliers(normalizeArray(suppRes.data));
      
      // Fetch forecast suggestions for all items
      try {
        const forecastRes = await axios.get(`${API}/forecast/suggestions?window=30`, { withCredentials: true });
        const forecastData = normalizeArray(forecastRes.data);
        const forecastMap = {};
        forecastData.forEach(f => { forecastMap[f.item_id] = f; });
        setForecasts(forecastMap);
      } catch (e) {
        console.error("Failed to fetch forecasts");
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
      // Ensure state stays as empty arrays on error
      setItems([]);
      setLocations([]);
      setDepartments([]);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Resolve IDs to display names for sheet storage
      const selLoc = locations.find(l => l.id === formData.location_id);
      const selDept = departments.find(d => d.id === formData.department_id);
      const selSup = suppliers.find(s => s.id === formData.supplier_id);

      const payload = {
        ...formData,
        location_id: formData.location_id || "",
        department_id: formData.department_id || "",
        supplier_id: formData.supplier_id || "",
        location_name: selLoc?.name || selLoc?.label || formData.location_name || "",
        department_name: selDept?.name || formData.department_name || "",
        supplier_name: selSup?.name || formData.supplier_name || "",
      };
      
      if (editingItem) {
        await axios.put(`${API}/items/${editingItem.id}`, payload, { 
          headers: getAuthHeader() 
        });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/items`, payload, { 
          headers: getAuthHeader() 
        });
        toast.success("Item created");
      }
      
      setSheetOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to save item");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      sku: "",
      unit: "each",
      location_id: "",
      department_id: "",
      supplier_id: "",
      supplier_url: "",
      on_hand: 0,
      reorder_point: 0,
      reorder_qty: 0,
      max_qty: 0,
      safety_stock: 0,
      depletion_qty: 1,
      lead_time_days: 7,
      tag_uid: "",
      qr_token: "",
      card_size: "",
      card_orientation: "portrait",
      image_url: ""
    });
    setEditingItem(null);
    setForecast(null);
  };

  const openEdit = async (item) => {
    setEditingItem(item);
    // Convert legacy rotation to orientation for backward compat
    let orientation = item.card_orientation || "portrait";
    if (!item.card_orientation && item.card_rotation) {
      orientation = (item.card_rotation === 90 || item.card_rotation === 270) ? "landscape" : "portrait";
    }
    
    // Reverse-map stored names to IDs for dropdown selection
    const matchLoc = locations.find(l => (l.name || l.label) === item.location_name) || {};
    const matchDept = departments.find(d => d.name === item.department_name) || {};
    const matchSup = suppliers.find(s => s.name === item.supplier_name) || {};

    setFormData({
      name: item.name,
      sku: item.sku,
      unit: item.unit || "each",
      location_id: item.location_id || matchLoc.id || "",
      department_id: item.department_id || matchDept.id || "",
      supplier_id: item.supplier_id || matchSup.id || "",
      supplier_url: item.supplier_url || "",
      location_name: item.location_name || "",
      department_name: item.department_name || "",
      supplier_name: item.supplier_name || "",
      on_hand: item.on_hand || 0,
      reorder_point: item.reorder_point || 0,
      reorder_qty: item.reorder_qty || 0,
      max_qty: item.max_qty || 0,
      safety_stock: item.safety_stock || 0,
      depletion_qty: item.depletion_qty || 1,
      lead_time_days: item.lead_time_days || 7,
      tag_uid: item.tag_uid || "",
      qr_token: item.qr_token || "",
      card_size: item.card_size || "",
      card_orientation: orientation,
      image_url: item.image_url || ""
    });
    setSheetOpen(true);
    
    // Fetch forecast
    try {
      const res = await axios.get(`${API}/items/${item.id}/forecast?window=30`, { 
        headers: getAuthHeader() 
      });
      setForecast(res.data);
    } catch (error) {
      console.error("Failed to fetch forecast");
      setForecast(null);
    }
  };

  const handleApplySuggestion = async (applyRop, applyOrderQty) => {
    if (!editingItem || !forecast) return;
    
    if (applyRop) setApplyingRop(true);
    if (applyOrderQty) setApplyingOrderQty(true);
    
    try {
      const res = await axios.post(
        `${API}/admin/items/${editingItem.id}/apply-suggestion`,
        { apply_rop: applyRop, apply_order_qty: applyOrderQty, window_days: 30 },
        { headers: getAuthHeader() }
      );
      
      if (res.data.success) {
        toast.success(`Applied: ${res.data.changes.join(", ")}`);
        
        // Update form data with new values
        if (applyRop) {
          setFormData(prev => ({ ...prev, reorder_point: forecast.suggested_rop }));
        }
        if (applyOrderQty) {
          setFormData(prev => ({ ...prev, reorder_qty: forecast.suggested_order_qty }));
        }
        
        // Refresh data
        fetchData();
      }
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to apply suggestion");
    } finally {
      setApplyingRop(false);
      setApplyingOrderQty(false);
    }
  };

  // Close PDF preview and cleanup blob URL
  const closePdfPreview = useCallback(() => {
    if (pdfPreview.blobUrl) {
      URL.revokeObjectURL(pdfPreview.blobUrl);
    }
    setPdfPreview({
      open: false,
      blobUrl: null,
      filename: "",
      itemName: ""
    });
  }, [pdfPreview.blobUrl]);

  // Open print dialog to select orientation
  const openPrintDialog = (item) => {
    // Convert legacy rotation to orientation for backward compat
    let orientation = item.card_orientation || "portrait";
    if (!item.card_orientation && item.card_rotation) {
      orientation = (item.card_rotation === 90 || item.card_rotation === 270) ? "landscape" : "portrait";
    }
    
    setPrintDialog({
      open: true,
      item,
      orientation,
      loading: false
    });
  };

  // Generate PDF with selected orientation
  const handleGeneratePdf = async () => {
    const { item, orientation } = printDialog;
    if (!item) return;

    setPrintDialog(prev => ({ ...prev, loading: true }));

    try {
      const token = localStorage.getItem("kanban_token");
      const response = await fetch(`${API}/items/${item.id}/card-pdf?orientation=${orientation}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to generate PDF' }));
        toast.error(`Card PDF failed: ${normalizeError(error) || 'Unknown error'}`);
        setPrintDialog(prev => ({ ...prev, loading: false }));
        return;
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Close print dialog
      setPrintDialog({ open: false, item: null, orientation: "portrait", loading: false });
      
      // Open preview modal
      setPdfPreview({
        open: true,
        blobUrl,
        filename: `kanban-card-${item.sku}.pdf`,
        itemName: `${item.name} (${item.sku})`
      });
      
      toast.success("Card PDF ready");
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Card PDF failed: Network error');
      setPrintDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Opens print dialog for single item
  const handlePrint = async (item) => {
    openPrintDialog(item);
  };

  // Batch PDF generation with preview modal
  const handleBatchPrint = async (itemIds, orientation = null) => {
    if (!itemIds || itemIds.length === 0) {
      toast.error('No items selected');
      return;
    }
    
    try {
      const token = localStorage.getItem("kanban_token");
      const body = { item_ids: itemIds };
      if (orientation) {
        body.orientation = orientation;
      }
      
      const response = await fetch(`${API}/items/batch-card-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to generate batch PDF' }));
        toast.error(`Batch PDF failed: ${normalizeError(error) || 'Unknown error'}`);
        return;
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Open preview modal
      setPdfPreview({
        open: true,
        blobUrl,
        filename: `kanban-cards-batch-${itemIds.length}.pdf`,
        itemName: `Batch (${itemIds.length} items)`
      });
      
      toast.success("Batch PDF ready");
    } catch (error) {
      console.error('Batch PDF generation error:', error);
      toast.error('Batch PDF failed: Network error');
    }
  };

  // Defensive guard: ensure items is always an array before filtering
  const safeItems = Array.isArray(items) ? items : [];
  
  const filteredItems = safeItems.filter(item =>
    item?.name?.toLowerCase().includes(search.toLowerCase()) ||
    item?.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const getStockStatus = (item) => {
    if (item.on_hand <= 0) return { label: "OUT", variant: "destructive" };
    if (item.on_hand <= item.reorder_point) return { label: "LOW", variant: "warning" };
    return { label: "OK", variant: "success" };
  };

  // P0.4: Check if user has admin role (supports multi-role)
  const isAdmin = user?.roles?.includes("admin") || user?.role === "admin";

  // P0.6: State for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteInput, setDeleteInput] = useState("");

  // P0.6: Handle item deletion
  const handleDeleteItem = async () => {
    if (!deleteConfirm || deleteInput !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    
    try {
      const res = await axios.delete(`${API}/items/${deleteConfirm.id}`, { headers: getAuthHeader() });
      toast.success(res.data.message || `Item "${deleteConfirm.name}" deleted`);
      if (res.data.had_history) {
        toast.info("Item history has been preserved");
      }
      setDeleteConfirm(null);
      setDeleteInput("");
      fetchData();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to delete item");
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div className="flex items-center gap-4">
          <h1 className="font-heading text-2xl tracking-tight">ITEMS</h1>
          <Badge variant="outline" className="font-mono">
            {items.length} total
          </Badge>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-64 industrial-input"
              data-testid="items-search-input"
            />
          </div>
          
          {isAdmin && (
            <Button 
              onClick={() => { resetForm(); setSheetOpen(true); }}
              className="rounded-sm uppercase tracking-wider"
              data-testid="add-item-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          )}
        </div>
      </div>
      
      {/* Table */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          <div className="industrial-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="industrial-table-header">
                  <TableHead className="w-12">STATUS</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>NAME</TableHead>
                  <TableHead className="text-right">ON HAND</TableHead>
                  <TableHead className="text-right">ROP</TableHead>
                  <TableHead className="text-right">
                    <span className="flex items-center justify-end gap-1">
                      <Lightbulb className="w-3 h-3 text-yellow-400" />
                      AVG/DAY
                    </span>
                  </TableHead>
                  <TableHead className="text-right">
                    <span className="flex items-center justify-end gap-1">
                      <Lightbulb className="w-3 h-3 text-yellow-400" />
                      SUGGESTED
                    </span>
                  </TableHead>
                  <TableHead>LOCATION</TableHead>
                  <TableHead className="w-24">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Package className="w-8 h-8" />
                        <span>No items found</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => {
                    const status = getStockStatus(item);
                    const fc = forecasts[item.id];
                    return (
                      <TableRow key={item.id} className="industrial-table-row" data-testid={`item-row-${item.sku}`}>
                        <TableCell>
                          <Badge 
                            variant={status.variant === "success" ? "outline" : "destructive"}
                            className={`industrial-badge ${
                              status.variant === "success" ? "bg-green-900/30 text-green-400 border-green-900" :
                              status.variant === "warning" ? "bg-yellow-900/30 text-yellow-400 border-yellow-900" :
                              "bg-red-900/30 text-red-400 border-red-900"
                            }`}
                          >
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right font-mono">
                          <span className={item.on_hand <= item.reorder_point ? "text-yellow-400" : ""}>
                            {item.on_hand}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {item.reorder_point}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {fc ? (
                            <span className={fc.confidence === "INSUFFICIENT" ? "text-muted-foreground" : "text-blue-400"}>
                              {fc.avg_daily_usage.toFixed(1)}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {fc ? (
                            <div className="flex flex-col items-end gap-0.5">
                              <span className={`font-mono text-xs ${
                                fc.suggested_rop !== item.reorder_point ? "text-yellow-400" : "text-muted-foreground"
                              }`}>
                                ROP: {fc.suggested_rop}
                              </span>
                              <span className={`font-mono text-xs ${fc.confidence === "INSUFFICIENT" ? "text-muted-foreground" : ""}`}>
                                {fc.confidence === "INSUFFICIENT" ? (
                                  <span className="text-muted-foreground italic">no data</span>
                                ) : fc.confidence === "LOW" ? (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 bg-yellow-900/20 text-yellow-400 border-yellow-900/50">LOW</Badge>
                                ) : null}
                              </span>
                            </div>
                          ) : "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.location_name || "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEdit(item)}
                                className="h-8 w-8"
                                data-testid={`edit-item-${item.sku}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePrint(item)}
                              className="h-8 w-8"
                              data-testid={`print-item-${item.sku}`}
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                            {/* P0.6: Delete button (Admin only) */}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteConfirm(item)}
                                className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                data-testid={`delete-item-${item.sku}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </ScrollArea>
      
      {/* Edit/Create Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) resetForm(); }}>
        <SheetContent className="w-[500px] sm:max-w-[500px] bg-card border-l border-border overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-heading text-xl tracking-tight">
              {editingItem ? "EDIT ITEM" : "NEW ITEM"}
            </SheetTitle>
            <SheetDescription>
              {editingItem ? "Update item details and inventory settings" : "Add a new item to inventory"}
            </SheetDescription>
          </SheetHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground border-b border-border pb-2">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="industrial-input"
                    data-testid="item-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">SKU *</Label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    required
                    className="industrial-input font-mono"
                    data-testid="item-sku-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Unit</Label>
                  <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                    <SelectTrigger className="industrial-input" data-testid="item-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="each">Each</SelectItem>
                      <SelectItem value="box">Box</SelectItem>
                      <SelectItem value="case">Case</SelectItem>
                      <SelectItem value="pallet">Pallet</SelectItem>
                      <SelectItem value="kg">Kilogram</SelectItem>
                      <SelectItem value="lb">Pound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Lead Time (days)</Label>
                  <Input
                    type="number"
                    value={formData.lead_time_days}
                    onChange={(e) => setFormData({ ...formData, lead_time_days: parseInt(e.target.value) || 0 })}
                    className="industrial-input font-mono"
                    data-testid="item-leadtime-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Organization */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground border-b border-border pb-2">
                Organization
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Location</Label>
                  <Select value={formData.location_id || "none"} onValueChange={(v) => setFormData({ ...formData, location_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="industrial-input" data-testid="item-location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Department</Label>
                  <Select value={formData.department_id || "none"} onValueChange={(v) => setFormData({ ...formData, department_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="industrial-input" data-testid="item-department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {departments.map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Supplier</Label>
                <Select value={formData.supplier_id || "none"} onValueChange={(v) => {
                  const sup = suppliers.find(s => s.id === v);
                  setFormData({
                    ...formData,
                    supplier_id: v === "none" ? "" : v,
                    supplier_url: (sup?.url && !formData.supplier_url) ? sup.url : formData.supplier_url,
                  });
                }}>
                  <SelectTrigger className="industrial-input" data-testid="item-supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {suppliers.map(sup => (
                      <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Supplier URL</Label>
                <Input
                  value={formData.supplier_url}
                  onChange={(e) => setFormData({ ...formData, supplier_url: e.target.value })}
                  placeholder="https://amazon.com/..."
                  className="industrial-input font-mono text-sm"
                  data-testid="item-supplier-url-input"
                />
              </div>
            </div>
            
            {/* Inventory Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground border-b border-border pb-2">
                Inventory Settings
              </h3>
              
              {editingItem && isAdmin && (
                <div className="space-y-2 p-3 border border-primary/30 rounded-sm bg-primary/5">
                  <Label className="text-xs uppercase tracking-wide text-primary font-bold">Current Quantity (Admin Only)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.on_hand}
                    onChange={(e) => setFormData({ ...formData, on_hand: parseInt(e.target.value) || 0 })}
                    className="industrial-input font-mono text-lg"
                    data-testid="item-current-qty-input"
                  />
                  <p className="text-xs text-muted-foreground">Directly set the on-hand quantity. Use with caution.</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Reorder Point</Label>
                  <Input
                    type="number"
                    value={formData.reorder_point}
                    onChange={(e) => setFormData({ ...formData, reorder_point: parseFloat(e.target.value) || 0 })}
                    className="industrial-input font-mono"
                    data-testid="item-rop-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Reorder Qty</Label>
                  <Input
                    type="number"
                    value={formData.reorder_qty}
                    onChange={(e) => setFormData({ ...formData, reorder_qty: parseFloat(e.target.value) || 0 })}
                    className="industrial-input font-mono"
                    data-testid="item-roq-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Safety Stock</Label>
                  <Input
                    type="number"
                    value={formData.safety_stock}
                    onChange={(e) => setFormData({ ...formData, safety_stock: parseFloat(e.target.value) || 0 })}
                    className="industrial-input font-mono"
                    data-testid="item-safety-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Max Qty</Label>
                  <Input
                    type="number"
                    value={formData.max_qty}
                    onChange={(e) => setFormData({ ...formData, max_qty: parseFloat(e.target.value) || 0 })}
                    className="industrial-input font-mono"
                    data-testid="item-max-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Depletion Qty (per scan)
                </Label>
                <Input
                  type="number"
                  value={formData.depletion_qty}
                  onChange={(e) => setFormData({ ...formData, depletion_qty: parseFloat(e.target.value) || 1 })}
                  className="industrial-input font-mono"
                  data-testid="item-depletion-input"
                />
              </div>
            </div>
            
            {/* Card Identifiers */}
            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground border-b border-border pb-2">
                Card Identifiers
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">NFC Tag UID</Label>
                  <Input
                    value={formData.tag_uid}
                    onChange={(e) => setFormData({ ...formData, tag_uid: e.target.value })}
                    placeholder="Auto or manual"
                    className="h-11 border-2 border-border/50 focus:border-primary font-mono"
                    data-testid="item-nfc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">QR Token</Label>
                  <Input
                    value={formData.qr_token}
                    onChange={(e) => setFormData({ ...formData, qr_token: e.target.value })}
                    placeholder="Auto-generated"
                    className="h-11 border-2 border-border/50 focus:border-primary font-mono"
                    data-testid="item-qr-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Item Image</Label>
                {formData.image_url ? (
                  <div className="p-2 bg-muted/30 rounded-sm space-y-2">
                    <img src={formData.image_url} alt="Item" className="max-h-24 object-contain rounded-sm" onError={(e) => e.target.style.display = 'none'} />
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="item-image-replace"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f || !editingItem) return;
                            const fd = new FormData();
                            fd.append("file", f);
                            try {
                              const res = await axios.post(`${API}/items/${editingItem.id}/image`, fd);
                              if (res.data.ok) { setFormData(prev => ({ ...prev, image_url: res.data.image_url })); toast.success("Image replaced"); }
                            } catch (err) { toast.error(err.response?.data?.detail || "Replace failed"); }
                          }}
                        />
                        <span className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline">Replace</span>
                      </label>
                      <button type="button" className="text-xs text-red-400 hover:underline" onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}>Remove</button>
                    </div>
                  </div>
                ) : editingItem ? (
                  <label className="cursor-pointer">
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="item-image-upload"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f || !editingItem) return;
                        const fd = new FormData();
                        fd.append("file", f);
                        try {
                          toast.info("Uploading...");
                          const res = await axios.post(`${API}/items/${editingItem.id}/image`, fd);
                          if (res.data.ok) { setFormData(prev => ({ ...prev, image_url: res.data.image_url })); toast.success("Image uploaded to Drive"); }
                        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                      }}
                    />
                    <span className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-sm text-sm text-muted-foreground hover:border-primary hover:text-primary cursor-pointer transition-colors">
                      Upload Image (PNG, JPG, WebP)
                    </span>
                  </label>
                ) : (
                  <p className="text-xs text-muted-foreground">Save item first, then upload an image.</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Card Size</Label>
                <Select 
                  value={formData.card_size || "default"} 
                  onValueChange={(v) => setFormData({ ...formData, card_size: v === "default" ? "" : v })}
                >
                  <SelectTrigger className="h-11 border-2 border-border/50 focus:border-primary" data-testid="item-card-size-select">
                    <SelectValue placeholder="Use global default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Use global default</SelectItem>
                    <Separator className="my-1" />
                    {cardSizes.map(size => (
                      <SelectItem key={size.id} value={size.id}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Override the default card size for PDF generation</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <RotateCw className="w-3 h-3" />
                  Default Card Orientation
                </Label>
                <Select 
                  value={formData.card_orientation || "portrait"} 
                  onValueChange={(v) => setFormData({ ...formData, card_orientation: v })}
                >
                  <SelectTrigger className="h-11 border-2 border-border/50 focus:border-primary" data-testid="item-card-orientation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait (Vertical)</SelectItem>
                    <SelectItem value="landscape">Landscape (Horizontal)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Default orientation when printing this item&apos;s card</p>
              </div>
            </div>
            
            {/* Forecast Suggestions */}
            {editingItem && forecast && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground border-b border-border pb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-yellow-400" />
                  Forecast Suggestions (30-Day)
                </h3>
                
                <div className="bg-muted/30 p-4 rounded-sm space-y-3">
                  {/* Confidence Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Confidence</span>
                    <Badge variant="outline" className={`text-xs ${
                      forecast.confidence === "HIGH" ? "bg-green-900/20 text-green-400 border-green-900/50" :
                      forecast.confidence === "LOW" ? "bg-yellow-900/20 text-yellow-400 border-yellow-900/50" :
                      "bg-red-900/20 text-red-400 border-red-900/50"
                    }`}>
                      {forecast.confidence}
                    </Badge>
                  </div>
                  
                  {/* Usage Stats */}
                  <div className="grid grid-cols-2 gap-4 text-center py-2">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Avg Daily Usage</p>
                      <p className="font-mono text-lg">{forecast.avg_daily_usage}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Available Days</p>
                      <p className="font-mono text-lg">{forecast.available_days}</p>
                      <p className="text-[10px] text-muted-foreground">(requested {forecast.window_days_requested})</p>
                    </div>
                  </div>
                  
                  {/* Notes */}
                  {forecast.notes && (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-background/50 p-2 rounded">
                      <Info className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>{forecast.notes}</span>
                    </div>
                  )}
                  
                  <Separator />
                  
                  {/* Suggested ROP */}
                  <div className="flex items-center justify-between p-3 bg-background rounded-sm">
                    <div>
                      <span className="text-xs text-muted-foreground block">Suggested ROP</span>
                      <span className="font-mono text-lg text-primary">{forecast.suggested_rop}</span>
                      {forecast.suggested_rop !== formData.reorder_point && (
                        <span className="text-xs text-muted-foreground ml-2">(current: {formData.reorder_point})</span>
                      )}
                    </div>
                    {(user?.role === "admin" || user?.role === "purchasing") && (
                      <Button
                        size="sm"
                        variant={forecast.suggested_rop === formData.reorder_point ? "secondary" : "default"}
                        disabled={applyingRop || forecast.suggested_rop === formData.reorder_point}
                        onClick={() => handleApplySuggestion(true, false)}
                        className="uppercase text-xs"
                        data-testid="apply-rop-btn"
                      >
                        {applyingRop ? "Applying..." : forecast.suggested_rop === formData.reorder_point ? (
                          <><Check className="w-3 h-3 mr-1" /> Applied</>
                        ) : "Apply"}
                      </Button>
                    )}
                  </div>
                  
                  {/* Suggested Order Qty */}
                  <div className="flex items-center justify-between p-3 bg-background rounded-sm">
                    <div>
                      <span className="text-xs text-muted-foreground block">Suggested Order Qty</span>
                      <span className="font-mono text-lg text-primary">{forecast.suggested_order_qty}</span>
                      {forecast.suggested_order_qty !== formData.reorder_qty && (
                        <span className="text-xs text-muted-foreground ml-2">(current: {formData.reorder_qty})</span>
                      )}
                    </div>
                    {(user?.role === "admin" || user?.role === "purchasing") && (
                      <Button
                        size="sm"
                        variant={forecast.suggested_order_qty === formData.reorder_qty ? "secondary" : "default"}
                        disabled={applyingOrderQty || forecast.suggested_order_qty === formData.reorder_qty}
                        onClick={() => handleApplySuggestion(false, true)}
                        className="uppercase text-xs"
                        data-testid="apply-order-qty-btn"
                      >
                        {applyingOrderQty ? "Applying..." : forecast.suggested_order_qty === formData.reorder_qty ? (
                          <><Check className="w-3 h-3 mr-1" /> Applied</>
                        ) : "Apply"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 uppercase tracking-wider rounded-sm" data-testid="save-item-btn">
                {editingItem ? "Update Item" : "Create Item"}
              </Button>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => setSheetOpen(false)}
                className="uppercase tracking-wider rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* P0.6: Delete Item Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) { setDeleteConfirm(null); setDeleteInput(""); } }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              DELETE ITEM
            </DialogTitle>
            <DialogDescription>
              This will soft-delete the item. It will be hidden from the inventory and cannot be scanned.
              Ledger history will be preserved.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-sm space-y-1">
              <p className="text-sm">
                You are about to delete: <span className="font-bold text-red-400">{deleteConfirm?.name}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                SKU: {deleteConfirm?.sku}
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground uppercase">Type DELETE to confirm</label>
              <Input
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="DELETE"
                className="industrial-input font-mono"
                data-testid="delete-item-confirm-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setDeleteConfirm(null); setDeleteInput(""); }} className="uppercase tracking-wider rounded-sm">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteItem} 
              disabled={deleteInput !== "DELETE"}
              className="uppercase tracking-wider rounded-sm"
              data-testid="confirm-delete-item-btn"
            >
              Delete Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Print Settings Dialog */}
      <Dialog open={printDialog.open} onOpenChange={(open) => !open && setPrintDialog({ open: false, item: null, orientation: "portrait", loading: false })}>
        <DialogContent className="sm:max-w-md bg-card border border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Printer className="w-5 h-5 text-primary" />
              Print Card
            </DialogTitle>
            <DialogDescription>
              Configure print settings for {printDialog.item?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Item Info */}
            <div className="bg-muted/30 p-3 rounded-sm">
              <p className="font-medium">{printDialog.item?.name}</p>
              <p className="text-sm text-muted-foreground font-mono">SKU: {printDialog.item?.sku}</p>
            </div>
            
            {/* Orientation Selection */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <RotateCw className="w-4 h-4" />
                Card Orientation
              </Label>
              <Select 
                value={printDialog.orientation} 
                onValueChange={(v) => setPrintDialog(prev => ({ ...prev, orientation: v }))}
              >
                <SelectTrigger className="h-11 border-2 border-border/50 focus:border-primary" data-testid="print-orientation-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">Portrait (Vertical)</SelectItem>
                  <SelectItem value="landscape">Landscape (Horizontal)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose the orientation for printing
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setPrintDialog({ open: false, item: null, orientation: "portrait", loading: false })}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleGeneratePdf}
              disabled={printDialog.loading}
              data-testid="generate-pdf-btn"
            >
              {printDialog.loading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Generating...
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4 mr-2" />
                  Generate PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* PDF Preview Modal */}
      <ItemCardPreviewModal
        open={pdfPreview.open}
        onClose={closePdfPreview}
        blobUrl={pdfPreview.blobUrl}
        filename={pdfPreview.filename}
        itemName={pdfPreview.itemName}
      />
    </div>
  );
}
